import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import { GET_ALL_PAYMENT_ORDER, GET_PAYMENT_ORDER_DETAIL } from "../../services/Endpoints";
import { IPaymentOrderFilter } from "../../services/payment-order/PaymentOrderService";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { handleError, sweetAlertError } from "../../utils/AppFunctions";
import { ORDER_KIND_LABEL_KEY } from "../../utils/PurchaseConstants";
import {
  CREDIT_TYPE_ENUM,
  GATEWAY_PAYMENT_STATUS_ENUM,
  ORDER_CONFIRMED_BY_ENUM,
  ORDER_DISPLAY_STATUS_ENUM,
  ORDER_HOLD_REASON_ENUM,
  ORDER_KIND_ENUM,
  PAYMENT_ALERT_TYPE_ENUM,
  WEBHOOK_EVENT_STATUS_ENUM,
} from "../../utils/PurchaseEnumerations";
import { formatAmountWithSign, formatPurchaseDateTime } from "../../utils/PurchaseFormat";

export interface IPaymentOrderRow {
  id: string;
  created_date: string;
  paid_date: string | null;
  firm_id: string;
  firm_name: string | null;
  order_kind: ORDER_KIND_ENUM;
  item_name: string;
  plan_type: string | null;
  credit_type: CREDIT_TYPE_ENUM | null;
  credits: number | null;
  total_amount: string;
  status: string;
  display_status: ORDER_DISPLAY_STATUS_ENUM;
  hold_reason: ORDER_HOLD_REASON_ENUM | null;
  gateway_order_id: string | null;
  invoice_number: string | null;
  receipt_number: string | null;
  method: string | null;
}

interface IPaymentOrderDetailOrder extends IPaymentOrderRow {
  base_amount: string;
  cgst_percent: string;
  sgst_percent: string;
  igst_percent: string;
  cgst_amount: string;
  sgst_amount: string;
  igst_amount: string;
  billing_name: string;
  billing_city: string | null;
  billing_address: string | null;
  billing_gstin: string | null;
  billing_state_name: string | null;
  created_by_name: string | null;
  business_information_version_no: number | null;
  paid_payment_id: string | null;
  validity: number | null;
  confirmed_by: ORDER_CONFIRMED_BY_ENUM | null;
}

export interface IOrderPayment {
  id: string;
  gateway_payment_id: string;
  status: GATEWAY_PAYMENT_STATUS_ENUM;
  amount_paise: string;
  method: string | null;
  bank: string | null;
  wallet: string | null;
  card_network: string | null;
  card_type: string | null;
  card_issuer: string | null;
  card_last4: string | null;
  error_reason: string | null;
  error_description: string | null;
  refund_status: string | null;
  gateway_created_at: string | null;
  created_date: string;
}

interface IOrderTimelineRow {
  id: string;
  from_status: string | null;
  to_status: string;
  source: string;
  note: string | null;
  created_date: string;
}

interface IOrderWebhookEvent {
  id: string;
  event_id: string;
  event_type: string;
  status: WEBHOOK_EVENT_STATUS_ENUM;
  attempts: number;
  last_error: string | null;
  received_at: string;
}

interface IOrderAlert {
  id: string;
  alert_type: PAYMENT_ALERT_TYPE_ENUM;
  sent_at: string | null;
  attempts: number;
  created_date: string;
}

export interface IPaymentOrderDetail {
  order: IPaymentOrderDetailOrder;
  payments: IOrderPayment[];
  timeline: IOrderTimelineRow[];
  webhook_events: IOrderWebhookEvent[];
  alerts: IOrderAlert[];
}

export const PAYMENT_ORDER_FILTER_INITIAL_VALUE: IPaymentOrderFilter = {
  status: ORDER_DISPLAY_STATUS_ENUM.Paid,
  order_kind: null,
  credit_type: null,
  firm_id: null,
  from_date: null,
  to_date: null,
};

const METHOD_LABEL_KEY: Record<string, string> = {
  upi: "method_upi",
  card: "method_card",
  netbanking: "method_netbanking",
  wallet: "method_wallet",
  emi: "method_emi",
  paylater: "method_paylater",
};

interface IPaymentOrderCells {
  action: (row: IPaymentOrderRow) => JSX.Element;
  status: (row: IPaymentOrderRow) => JSX.Element;
  razorpayOrder: (row: IPaymentOrderRow) => JSX.Element;
}

export const getMethodLabel = (t: TUseTranslationTfn, method: string | null): string => {
  if (!method) return "-";
  const key = METHOD_LABEL_KEY[method];
  return key ? t(key) : method.charAt(0).toUpperCase() + method.slice(1);
};

export const getOrderRowClass = (row: IPaymentOrderRow) =>
  row.display_status === ORDER_DISPLAY_STATUS_ENUM.Duplicate ? "bg-warning-muted" : undefined;

export const getPaymentOrderFields = (
  cells: IPaymentOrderCells,
  t: TUseTranslationTfn,
): ITableFields<IPaymentOrderRow>[] => [
  {
    name: t("date"),
    key: "created_date",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => formatPurchaseDateTime(data.created_date),
  },
  {
    name: t("razorpay_order"),
    key: "gateway_order_id",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-3",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => cells.razorpayOrder(data),
  },
  {
    name: t("firm_name"),
    key: "firm_name",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => data.firm_name ?? "-",
  },
  {
    name: t("item"),
    key: "item_name",
    sort: false,
    type: TABLE_FIELD_TYPE.text,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
  },
  {
    name: t("plan_type"),
    key: "plan_type",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => data.plan_type ?? "-",
  },
  {
    name: t("kind"),
    key: "order_kind",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => {
      const row: IPaymentOrderRow = data;
      return t(ORDER_KIND_LABEL_KEY[row.order_kind]);
    },
  },
  {
    name: t("amount"),
    key: "total_amount",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => formatAmountWithSign(data.total_amount),
  },
  {
    name: t("status"),
    key: "display_status",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => cells.status(data),
  },
  {
    name: t("method"),
    key: "method",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IPaymentOrderRow>) => getMethodLabel(t, data.method),
  },
  {
    name: t("action"),
    key: "action",
    sort: false,
    type: TABLE_FIELD_TYPE.custom,
    align: TABLE_FIELD_ALIGN.center,
    flex: "col-span-2",
    component: cells.action,
  },
];

export const getAllPaymentOrder = async (
  setList: TReactSetState<IPaymentOrderRow[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  filter: IPaymentOrderFilter,
) => {
  try {
    const response = await GET_ALL_PAYMENT_ORDER(pagination, search, filter);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      setList(response.data.item ?? []);
      setPagination(response.data.pagination);
      return;
    }
    setList([]);
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "getAllPaymentOrder");
  }
};

export const fetchPaymentOrderDetail = async (
  id: string,
): Promise<IPaymentOrderDetail | undefined> => {
  try {
    const response = await GET_PAYMENT_ORDER_DETAIL(id);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      return response.data;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "fetchPaymentOrderDetail");
  }
  return undefined;
};
